import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import {
    LocationOn,
    ArrowBack,
    Map,
    Home,
    Work,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserById, getUserAddresses } from '../../services/Api';

const ViewUserAddress = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // Data state
    const [user, setUser] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalAddresses, setTotalAddresses] = useState(0);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!user) {
                const userResp = await getUserById(id);
                if (userResp.success) setUser(userResp.data);
            }

            const addrResp = await getUserAddresses(id, page + 1, rowsPerPage);
            if (addrResp.success) {
                setAddresses(addrResp.data || []);
                setTotalAddresses(addrResp.totalCount || addrResp.count || addrResp.data?.length || 0);
            } else {
                setError(addrResp.message || 'Failed to fetch addresses');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getAddressTypeDetails = (type) => {
        const t = type?.toLowerCase();
        if (t === 'home') return { icon: <Home fontSize="small" />, color: 'success' };
        if (t === 'work') return { icon: <Work fontSize="small" />, color: 'primary' };
        return { icon: <LocationOn fontSize="small" />, color: 'secondary' };
    };

    if (loading && !user && addresses.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => { e.preventDefault(); navigate('/users'); }}
                    >
                        Users
                    </Link>
                    <Typography color="text.primary">User Addresses</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                            <Map sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                            Saved Addresses
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Managing locations for {user?.name || 'User'}
                        </Typography>
                    </Box>
                    <Button 
                        startIcon={<ArrowBack />} 
                        onClick={() => navigate('/users')}
                        variant="outlined"
                        sx={{ borderRadius: '8px' }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table aria-label="user addresses table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Full Address</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>City & State</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Pincode</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Coordinates</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>isDefault</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && addresses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : addresses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No saved addresses found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                addresses.map((addr) => {
                                    const { icon, color } = getAddressTypeDetails(addr.addressType);
                                    return (
                                        <TableRow key={addr.id} hover>
                                            <TableCell>
                                                <Chip 
                                                    icon={icon} 
                                                    label={addr.addressType || 'Other'} 
                                                    size="small" 
                                                    color={color}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 250 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {addr.street}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {addr.area}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{addr.city}, {addr.state}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{addr.pincode}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                    Lat: {Number(addr.latitude).toFixed(4)}<br />
                                                    Lng: {Number(addr.longitude).toFixed(4)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {addr.isDefault ? "true" : "false"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalAddresses}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default ViewUserAddress;
